import React from 'react';
import BackButton from '../components/BackButton';

const StudyScreen = ({ onBack }) => {
    return (
        <div className="flex flex-col h-screen overflow-hidden bg-white">
            {/* Navigation Bar */}
            <nav className="w-full bg-[#1c1d1f] text-white px-4 py-3 flex items-center justify-between z-50 shrink-0">
                <div className="flex items-center gap-6">
                    <div className="flex items-center gap-4 border-r border-gray-700 pr-6">
                        <BackButton onClick={onBack} title="Go Back" className="!bg-gray-800 !text-gray-300 hover:!text-white" />
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded bg-primary flex items-center justify-center text-white font-bold text-lg">H</div>
                            <span className="font-display font-bold text-lg tracking-tight">Himigo</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-gray-300">Mathematics - Linear Equations</span>
                    </div>
                </div>
                <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2 text-sm">
                        <span className="material-symbols-outlined text-yellow-400">emoji_events</span>
                        <span className="font-bold">Your progress: 45%</span>
                    </div>
                    <button className="flex items-center gap-1 text-sm font-bold border border-gray-600 px-3 py-1.5 rounded hover:bg-white/10 transition-colors">
                        <span className="material-symbols-outlined text-sm">share</span> Share
                    </button>
                    <div className="flex items-center gap-3 border-l border-gray-700 pl-6">
                        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold overflow-hidden">
                            <img alt="Profile" className="w-full h-full object-cover" src="https://lh3.googleusercontent.com/aida-public/AB6AXuAcX0IpNjjg8Nzgf-UK5yfCdomrtyx1uLC9beaKZP6pBEdK7TlcRtjk-MyUpENe6dUmUudgvaOhDgMrrzA9HL2f2Wh5eWqcdvTOeovcilMxVRu7SX7_3b8y1-AaTWX9diRhYjBbC-aCxBz8KJcmWIs2z7Vt1ROrWYW1hAmUdAMk7JZyoDge0czxmwzDZGSk3CJds-FeGiaH7jwwlfFS_V4lbdttk_3bTMMJTBhoqf0ecw6uor-9n7E-js1g1638nV5BmCQ5yvhkarU" />
                        </div>
                    </div>
                </div>
            </nav>

            <div className="flex flex-1 overflow-hidden">
                {/* Main Content Area */}
                <main className="flex-1 flex flex-col overflow-hidden bg-white">
                    <div className="flex-1 overflow-y-auto">
                        <div className="max-w-[800px] mx-auto px-8 py-12">
                            <nav className="flex items-center gap-2 text-primary font-semibold text-sm mb-8">
                                <span>Mathematics</span>
                                <span className="material-symbols-outlined text-xs">chevron_right</span>
                                <span className="text-gray-500">Section 2: Fractions & Decimals</span>
                            </nav>

                            <h1 className="text-3xl font-bold mb-6 font-display text-[#1c1d1f]">Mathematics - Linear Equations</h1>
                            <p className="text-lg leading-relaxed mb-4 text-gray-700">Linear equations are the foundation of algebraic thinking. At its simplest, a linear equation is an equation for a straight line. It establishes a relationship between variables that, when plotted on a graph, forms a continuous straight path.</p>

                            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 my-8 rounded-r-lg">
                                <div className="flex items-center gap-2 text-blue-700 font-bold mb-2">
                                    <span className="material-symbols-outlined">lightbulb</span>
                                    Important Concept: The Standard Form
                                </div>
                                <p className="text-blue-900 mb-0">The standard form for linear equations in two variables is <strong>Ax + By = C</strong>. In this equation, x and y are variables, and A, B, and C are constants.</p>
                            </div>

                            <h2 className="text-2xl font-bold mt-12 mb-4 font-display text-[#1c1d1f]">Interactive Practice</h2>
                            <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 my-10 space-y-8">
                                <div className="space-y-4">
                                    <p className="font-bold text-gray-900">1. Which of the following is a linear equation?</p>
                                    <div className="grid grid-cols-1 gap-3">
                                        <label className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg bg-white cursor-pointer hover:border-primary transition-all">
                                            <input className="text-primary focus:ring-primary h-4 w-4" name="mcq" type="radio" />
                                            <span className="text-sm">y = x² + 2</span>
                                        </label>
                                        <label className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg bg-white cursor-pointer hover:border-primary transition-all">
                                            <input className="text-primary focus:ring-primary h-4 w-4" name="mcq" type="radio" />
                                            <span className="text-sm">3x + 4y = 12</span>
                                        </label>
                                        <label className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg bg-white cursor-pointer hover:border-primary transition-all">
                                            <input className="text-primary focus:ring-primary h-4 w-4" name="mcq" type="radio" />
                                            <span className="text-sm">y = 1/x</span>
                                        </label>
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    <p className="font-bold text-gray-900">2. Fill in the blanks</p>
                                    <div className="text-lg leading-loose">
                                        In the equation y = mx + c, the letter 'm' represents the
                                        <input className="border-b-2 border-gray-300 border-t-0 border-l-0 border-r-0 focus:ring-0 focus:border-primary w-32 px-1 text-center bg-transparent" placeholder="......." type="text" />
                                        and 'c' represents the
                                        <input className="border-b-2 border-gray-300 border-t-0 border-l-0 border-r-0 focus:ring-0 focus:border-primary w-32 px-1 text-center bg-transparent" placeholder="......." type="text" />.
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    <p className="font-bold text-gray-900">3. Explain the process of isolating a variable in your own words.</p>
                                    <textarea className="w-full rounded-lg border-gray-300 focus:border-primary focus:ring-primary p-4 min-h-[120px]" placeholder="Type your answer here..."></textarea>
                                    <div className="flex justify-end">
                                        <button className="bg-primary hover:bg-primary-dark text-white font-bold py-2 px-6 rounded-lg flex items-center gap-2 transition-colors">
                                            <span className="material-symbols-outlined text-sm">auto_awesome</span>
                                            AI Grade & Feedback
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <h2 className="text-2xl font-bold mt-12 mb-4 font-display text-[#1c1d1f]">Key Properties</h2>
                            <ul className="list-disc pl-6 mb-6 space-y-2 text-lg text-gray-700">
                                <li>The highest power of the variable is always 1.</li>
                                <li>The graph of the equation is always a straight line.</li>
                                <li>It has a constant slope (uniform rate of change).</li>
                            </ul>
                        </div>
                    </div>

                    {/* AI Tools Navbar (Sticky at bottom of main) */}
                    <div className="border-t border-gray-200 shrink-0 bg-white shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
                        <div className="max-w-[1200px] mx-auto px-8">
                            <div className="flex border-b border-gray-200 overflow-x-auto no-scrollbar">
                                <button className="px-6 py-4 text-sm font-bold border-b-2 border-primary text-primary whitespace-nowrap flex items-center gap-2">
                                    <span className="material-symbols-outlined text-lg">smart_toy</span> AI Tutor
                                </button>
                                <button className="px-6 py-4 text-sm font-bold text-gray-500 hover:text-gray-900 transition-colors whitespace-nowrap flex items-center gap-2">
                                    <span className="material-symbols-outlined text-lg">quiz</span> Quiz Generator
                                </button>
                                <button className="px-6 py-4 text-sm font-bold text-gray-500 hover:text-gray-900 transition-colors whitespace-nowrap flex items-center gap-2">
                                    <span className="material-symbols-outlined text-lg">summarize</span> Summaries
                                </button>
                                <button className="px-6 py-4 text-sm font-bold text-gray-500 hover:text-gray-900 transition-colors whitespace-nowrap flex items-center gap-2">
                                    <span className="material-symbols-outlined text-lg">edit_note</span> Personal Notes
                                </button>
                                <button className="px-6 py-4 text-sm font-bold text-gray-500 hover:text-gray-900 transition-colors whitespace-nowrap flex items-center gap-2">
                                    <span className="material-symbols-outlined text-lg">psychology</span> Mnemonics
                                </button>
                                <button className="px-6 py-4 text-sm font-bold text-gray-500 hover:text-gray-900 transition-colors whitespace-nowrap flex items-center gap-2">
                                    <span className="material-symbols-outlined text-lg">account_tree</span> Mind Maps
                                </button>
                            </div>
                            <div className="py-8 h-40 flex items-center justify-center text-gray-400">
                                <div className="text-center">
                                    <span className="material-symbols-outlined text-4xl mb-2">auto_awesome</span>
                                    <p className="text-sm">Select a tool to activate AI assistance</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </main>

                {/* Sidebar - Course Content */}
                <aside className="w-[350px] border-l border-gray-200 bg-white flex flex-col shrink-0">
                    <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-white">
                        <h3 className="font-bold text-gray-900">Course content</h3>
                        <button className="text-gray-500 hover:text-gray-900 transition-colors"><span className="material-symbols-outlined">close</span></button>
                    </div>
                    <div className="flex-1 overflow-y-auto custom-scrollbar">
                        <div className="border-b border-gray-200">
                            <button className="w-full p-4 bg-gray-50 flex flex-col gap-1 items-start text-left">
                                <div className="w-full flex justify-between items-center">
                                    <span className="font-bold text-sm">Section 1: Integers</span>
                                    <span className="material-symbols-outlined text-gray-400">expand_less</span>
                                </div>
                                <span className="text-xs text-gray-500">4 / 4 | 15min</span>
                            </button>
                        </div>
                        <div className="border-b border-gray-200">
                            <button className="w-full p-4 bg-white flex flex-col gap-1 items-start text-left">
                                <div className="w-full flex justify-between items-center">
                                    <span className="font-bold text-sm">Section 2: Fractions & Decimals</span>
                                    <span className="material-symbols-outlined text-gray-400">expand_less</span>
                                </div>
                                <span className="text-xs text-gray-500">2 / 6 | 45min</span>
                            </button>
                            <div className="bg-gray-100/50">
                                <div className="p-4 flex gap-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 bg-white transition-colors">
                                    <span className="material-symbols-outlined text-primary text-xl">check_circle</span>
                                    <div className="flex-1">
                                        <p className="text-sm font-medium">3. Proper and Improper Fractions</p>
                                    </div>
                                </div>
                                <div className="p-4 flex gap-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 bg-blue-50 transition-colors">
                                    <span className="material-symbols-outlined text-gray-400 text-xl">radio_button_unchecked</span>
                                    <div className="flex-1">
                                        <p className="text-sm font-bold text-gray-900">4. Linear Equations Basics</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* AI Quick Actions Section */}
                        <div className="p-6 bg-white border-t border-gray-100">
                            <div className="flex items-center gap-2 mb-4">
                                <span className="material-symbols-outlined text-primary">smart_toy</span>
                                <h4 className="font-bold text-sm text-[#1c1d1f]">AI Quick Actions</h4>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <button className="flex flex-col items-center justify-center p-4 border border-gray-100 rounded-xl hover:bg-gray-50 transition-colors shadow-sm bg-white group">
                                    <span className="material-symbols-outlined text-primary mb-2 group-hover:scale-110 transition-transform">chat</span>
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-700">Ask</span>
                                </button>
                                <button className="flex flex-col items-center justify-center p-4 border border-gray-100 rounded-xl hover:bg-gray-50 transition-colors shadow-sm bg-white group">
                                    <span className="material-symbols-outlined text-primary mb-2 group-hover:scale-110 transition-transform">menu_book</span>
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-700">Summarize</span>
                                </button>
                                <button className="flex flex-col items-center justify-center p-4 border border-gray-100 rounded-xl hover:bg-gray-50 transition-colors shadow-sm bg-white group">
                                    <span className="material-symbols-outlined text-primary mb-2 group-hover:scale-110 transition-transform">psychology</span>
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-700">Mnemonics</span>
                                </button>
                                <button className="flex flex-col items-center justify-center p-4 border border-gray-100 rounded-xl hover:bg-gray-50 transition-colors shadow-sm bg-white group">
                                    <span className="material-symbols-outlined text-primary mb-2 group-hover:scale-110 transition-transform">share</span>
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-700">Map</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="p-4 border-t border-gray-200">
                        <button className="w-full py-3 bg-white border-2 border-primary text-primary font-bold rounded hover:bg-primary-light transition-colors flex items-center justify-center gap-2 text-sm">
                            <span className="material-symbols-outlined text-lg">description</span> Download Resources
                        </button>
                    </div>
                </aside>
            </div>

            <style jsx>{`
                .custom-scrollbar::-webkit-scrollbar {
                    width: 4px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: #d1d5db;
                    border-radius: 10px;
                }
                .no-scrollbar::-webkit-scrollbar {
                    display: none;
                }
                .no-scrollbar {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
            `}</style>
        </div>
    );
};

export default StudyScreen;
